import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut as firebaseSignOut, User } from 'firebase/auth'
import { getAnalytics } from 'firebase/analytics'
import { getMessaging, getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging'
import type { Messaging } from 'firebase/messaging'

const firebaseConfig = {
  apiKey: "AIzaSyDFV1sEqpjJfkd56kr41rXzlPfZC3ZHUqI",
  authDomain: "easyservicebf.firebaseapp.com",
  projectId: "easyservicebf",
  storageBucket: "easyservicebf.firebasestorage.app",
  messagingSenderId: "554662731112",
  appId: "1:554662731112:web:94acad912f84878585cadd",
  measurementId: "G-6PTRCTB3MJ"
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)

// Initialize analytics only in browser
let analytics: any = null
if (typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app)
  } catch (error) {
    console.warn('Firebase Analytics is not supported in this environment')
  }
}
export { analytics }

// Initialize messaging conditionally
let messaging: Messaging | null = null

const initializeMessaging = async () => {
  try {
    const supported = await isSupported()
    if (supported) {
      messaging = getMessaging(app)
      console.log('Firebase Messaging initialized successfully')
    } else {
      console.warn('Firebase Messaging is not supported in this browser')
    }
  } catch (error) {
    console.warn('Firebase messaging initialization failed:', error)
  }
}

// Initialize messaging immediately
initializeMessaging()

export { messaging }

// Check if messaging is supported
export const isMessagingSupported = async (): Promise<boolean> => {
  try {
    return await isSupported()
  } catch (error) {
    console.error('Error checking messaging support:', error)
    return false
  }
}

// Request notification permission
export const requestPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications')
  }

  const permission = await Notification.requestPermission()
  console.log('Notification permission:', permission)
  return permission
}

// Get current notification permission
export const getNotificationPermission = (): NotificationPermission => {
  if (!('Notification' in window)) {
    return 'denied'
  }
  return Notification.permission
}

export const signInWithGoogle = async (): Promise<User> => {
  try {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)
    return result.user
  } catch (error) {
    console.error('Error signing in with Google:', error)
    throw error
  }
}

export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth)
  } catch (error) {
    console.error('Error signing out:', error)
    throw error
  }
}

export const getCurrentUser = (): User | null => {
  return auth.currentUser
}

/**
 * Get FCM token for push notifications
 * @param vapidKey - The VAPID key from Firebase Console
 */
export const getFCMToken = async (vapidKey?: string): Promise<string | null> => {
  if (!messaging) {
    console.error('Firebase messaging is not initialized')
    return null
  }

  try {
    // Check if permission is granted
    const permission = Notification.permission
    if (permission !== 'granted') {
      console.log('Notification permission not granted:', permission)
      return null
    }

    const token = await getToken(messaging, {
      vapidKey: vapidKey || process.env.VITE_FIREBASE_VAPID_KEY || 'YOUR_VAPID_KEY'
    })

    if (token) {
      console.log('FCM token obtained successfully')
      return token
    } else {
      console.log('No FCM token available')
      return null
    }
  } catch (error) {
    console.error('Error getting FCM token:', error)
    return null
  }
}

/**
 * Delete FCM token (unsubscribe from notifications)
 */
export const deleteFCMToken = async (): Promise<boolean> => {
  if (!messaging) {
    console.error('Firebase messaging is not initialized')
    return false
  }

  try {
    await deleteToken(messaging)
    console.log('FCM token deleted successfully')
    return true
  } catch (error) {
    console.error('Error deleting FCM token:', error)
    return false
  }
}

/**
 * Listen for foreground messages
 */
export const onMessageListener = (): Promise<any> => {
  if (!messaging) {
    console.warn('Firebase messaging not available')
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    const unsubscribe = onMessage(messaging as any, (payload) => {
      console.log('Received foreground message:', payload)
      resolve(payload)
    })

    // Cleanup after 60 seconds to prevent memory leaks
    setTimeout(() => {
      unsubscribe()
    }, 60000)
  })
}

/**
 * Refresh FCM token
 */
export const refreshFCMToken = async (vapidKey?: string): Promise<string | null> => {
  if (!messaging) {
    console.error('Firebase messaging is not initialized')
    return null
  }

  try {
    // Delete old token
    await deleteFCMToken()

    // Get new token
    const newToken = await getFCMToken(vapidKey)
    return newToken
  } catch (error) {
    console.error('Error refreshing FCM token:', error)
    return null
  }
}

export { app }